"use client";

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Input } from "../../ui/input";
import { ArrowLeft, Search, Upload, Loader2, Play, Plus, Save, MessageSquare } from "lucide-react";
import { getFY_YEAR } from "@/utils/sharepoint-env";
import { ReviewComment } from "../../shared/ReviewComment";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Badge } from "../../ui/badge";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useEngagement } from "@/contexts/EngagementContext";
import { 
  getProcedureFileUsageForWorkspace, 
  updateProcedureFileUsageForWorkspace,
  type FileUsageEntry 
} from "@/utils/fileUsageReference";

interface InvPhysicalVerificationProps {
  onBack?: () => void;
  workspaceName?: string;
  procedureName?: string;
}
interface ExcelColumn { name: string; value: string; }

export default function InvPhysicalVerification({ onBack, workspaceName, procedureName }: InvPhysicalVerificationProps) {
  // File discovery states
  const [irStatus, setIrStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [mcrStatus, setMcrStatus] = useState<"idle" | "searching" | "found" | "not_found">("idle");
  const [irFiles, setIrFiles] = useState<string[]>([]);
  const [mcrFiles, setMcrFiles] = useState<string[]>([]);
  const [irSelected, setIrSelected] = useState<string>("");
  const [mcrSelected, setMcrSelected] = useState<string>("");
  const [refIR, setRefIR] = useState<string>("");
  const [refMCR, setRefMCR] = useState<string>("");
  const [upIR, setUpIR] = useState<"idle" | "uploading" | "completed" | "error">("idle");
  const [upMCR, setUpMCR] = useState<"idle" | "uploading" | "completed" | "error">("idle");

  // Columns
  const [irColumns, setIrColumns] = useState<ExcelColumn[]>([]);
  const [mcrColumns, setMcrColumns] = useState<ExcelColumn[]>([]);
  const [loadingColsIR, setLoadingColsIR] = useState(false);
  const [loadingColsMCR, setLoadingColsMCR] = useState(false);

  // Unique columns
  const [irUniqueCol, setIrUniqueCol] = useState<string>("");
  const [mcrUniqueCol, setMcrUniqueCol] = useState<string>("");

  // Column mapping table rows
  const [rows, setRows] = useState<Array<{ left?: string; right?: string }>>([{}]);

  // Run state
  const [status, setStatus] = useState<"idle" | "running" | "completed" | "error">("idle");
  // Questionnaire states
  const { currentEngagement } = useEngagement();
  const [companyName, setCompanyName] = useState<string>("");
  const [areaPrefix, setAreaPrefix] = useState<string>("INV");
  const [managementCountDate, setManagementCountDate] = useState<string>("");
  const [auditorCountDate, setAuditorCountDate] = useState<string>("");
  const [questionResponses, setQuestionResponses] = useState<Record<string, {
    selectedOption?: string;
    textResponse?: { html: string; plainText: string } | null;
    documents?: Array<{ id: string; type: string; name: string; content?: string; url?: string }>;
  }>>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [fileUsageReference, setFileUsageReference] = useState<FileUsageEntry[]>([]);
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `INV_PHYSICAL_VERIFICATION_${normalizedSection}`;
  };

  const loadFileUsageReference = async () => {
    try {
      const usage = await getProcedureFileUsageForWorkspace("Inventory", "Physical Verification");
      setFileUsageReference(usage);
    } catch (error) {
      console.error("Error loading file usage reference:", error);
    }
  };

  // Auto-trigger both searches on mount
  useEffect(() => {
    searchIR();
    searchMCR();
    loadFileUsageReference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchIR = async () => {
    setIrStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const res = await window.sharePointAPI.loadComcFiles("Execution_INV_IR");
        if (res.success && res.data?.files?.length) {
          setIrFiles(res.data.files.map((f: any) => f.name).filter(Boolean));
          setIrStatus("found");
        } else setIrStatus("not_found");
      } else setIrStatus("not_found");
    } catch { setIrStatus("not_found"); }
  };

  const searchMCR = async () => {
    setMcrStatus("searching");
    try {
      if (window.sharePointAPI?.loadComcFiles) {
        const res = await window.sharePointAPI.loadComcFiles("Execution_INV_MCR");
        if (res.success && res.data?.files?.length) {
          setMcrFiles(res.data.files.map((f: any) => f.name).filter(Boolean));
          setMcrStatus("found");
        } else setMcrStatus("not_found");
      } else setMcrStatus("not_found");
    } catch { setMcrStatus("not_found"); }
  };

  const uploadIR = async () => {
    setUpIR("uploading");
    try {
      const input = document.createElement('input'); input.type='file'; input.accept='.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0]; if (!file) return;
        try {
          const base64 = await new Promise<string>((resolve, reject) => { const r=new FileReader(); r.onload=()=>resolve((r.result as string).split(',')[1]); r.onerror=()=>reject(); r.readAsDataURL(file); });
          const api = window.sharePointAPI; if (!api?.uploadFile) throw new Error('api');
          const result = await api.uploadFile({ fileContent: base64, fileName: file.name, referenceValue: refIR, folderName: "client", fyYear: getFY_YEAR(), sectionList: ["Execution_INV_IR"] });
          if (result.success) { setIrSelected(file.name); setIrFiles([file.name]); setIrStatus("found"); setUpIR("completed"); } else setUpIR("error");
        } catch { setUpIR("error"); }
      };
      input.click();
    } catch { setUpIR("error"); }
  };

  const uploadMCR = async () => {
    setUpMCR("uploading");
    try {
      const input = document.createElement('input'); input.type='file'; input.accept='.xlsx,.xls,.csv';
      input.onchange = async (e) => {
        const file = (e.target as HTMLInputElement).files?.[0]; if (!file) return;
        try {
          const base64 = await new Promise<string>((resolve, reject) => { const r=new FileReader(); r.onload=()=>resolve((r.result as string).split(',')[1]); r.onerror=()=>reject(); r.readAsDataURL(file); });
          const api = window.sharePointAPI; if (!api?.uploadFile) throw new Error('api');
          const result = await api.uploadFile({ fileContent: base64, fileName: file.name, referenceValue: refMCR, folderName: "client", fyYear: getFY_YEAR(), sectionList: ["Execution_INV_MCR"] });
          if (result.success) { setMcrSelected(file.name); setMcrFiles([file.name]); setMcrStatus("found"); setUpMCR("completed"); } else setUpMCR("error");
        } catch { setUpMCR("error"); }
      };
      input.click();
    } catch { setUpMCR("error"); }
  };

  const loadIRColumns = async () => {
    if (!irSelected) return; setLoadingColsIR(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const res = await window.payroll.loadExcelColumns(irSelected);
        if (res.ok && res.columns) {
          setIrColumns(res.columns.map((c: string)=>({name:c, value:c})));
          // Save file name to file usage reference
          const fileName = irSelected.includes(' (') ? irSelected.split(' (')[0] : irSelected;
          await updateProcedureFileUsageForWorkspace("Inventory", "Physical Verification", [{ 
            "Search files - Inventory Register": fileName 
          }]);
          await loadFileUsageReference();
        }
      }
    } finally { setLoadingColsIR(false); }
  };

  const loadMCRColumns = async () => {
    if (!mcrSelected) return; setLoadingColsMCR(true);
    try {
      if (window.payroll?.loadExcelColumns) {
        const res = await window.payroll.loadExcelColumns(mcrSelected);
        if (res.ok && res.columns) {
          setMcrColumns(res.columns.map((c: string)=>({name:c, value:c})));
          // Save file name to file usage reference
          const fileName = mcrSelected.includes(' (') ? mcrSelected.split(' (')[0] : mcrSelected;
          await updateProcedureFileUsageForWorkspace("Inventory", "Physical Verification", [{ 
            "Search files - Management Count Report": fileName 
          }]);
          await loadFileUsageReference();
        }
      }
    } finally { setLoadingColsMCR(false); }
  };

  const canRun = useMemo(() => {
    const allPairsFilled = rows.length>0 && rows.every(r => r.left && r.right);
    return irSelected && mcrSelected && irUniqueCol && mcrUniqueCol && allPairsFilled;
  }, [irSelected, mcrSelected, irUniqueCol, mcrUniqueCol, rows]);

  // Load company name and area prefix
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn("Failed to load company name, using fallback");
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  useEffect(() => {
    const loadAreaPrefix = async () => {
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data) {
            const sectionsData = result.data;
            if (sectionsData.Execution && sectionsData.Execution["Inventory"]) {
              setAreaPrefix(sectionsData.Execution["Inventory"]);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to load area prefix from Sections.json, using default INV:", error);
      }
    };
    loadAreaPrefix();
  }, []);

  // Load saved questionnaire responses
  useEffect(() => {
    const loadSavedResponses = async () => {
      if (!companyName || !areaPrefix) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const fileName = `Execution_${areaPrefix}_Responses.json`;
        const result = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);

        if (result?.success && result?.data?.substantiveProcedures) {
          const savedData = result.data.substantiveProcedures;
          
          // Load dates (convert from ISO or any format to YYYY-MM-DD for date input)
          if (savedData["physical-verification-management-date"]) {
            const dateStr = savedData["physical-verification-management-date"].textResponse?.plainText || "";
            if (dateStr) {
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                // Format as YYYY-MM-DD for HTML date input
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setManagementCountDate(`${year}-${month}-${day}`);
              } else {
                setManagementCountDate(dateStr);
              }
            }
          }
          if (savedData["physical-verification-auditor-date"]) {
            const dateStr = savedData["physical-verification-auditor-date"].textResponse?.plainText || "";
            if (dateStr) {
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                // Format as YYYY-MM-DD for HTML date input
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                setAuditorCountDate(`${year}-${month}-${day}`);
              } else {
                setAuditorCountDate(dateStr);
              }
            }
          }

          // Load question responses
          const questionIds = [
            "physical-verification-evidence",
            "physical-verification-inventory-changes",
            "physical-verification-auditor-different",
            "physical-verification-auditor-blank",
            "physical-verification-sufficient-evidence",
            "physical-verification-third-party",
            "physical-verification-third-party-doubt",
            "physical-verification-third-party-procedures"
          ];

          const loadedResponses: Record<string, any> = {};
          questionIds.forEach(qId => {
            const saved = savedData[qId];
            if (saved) {
              loadedResponses[qId] = {
                selectedOption: saved.selectedOption || "",
                textResponse: saved.textResponse || null,
                documents: (saved.documents || []).map((docName: string) => ({
                  id: `doc_${docName}`,
                  type: 'linked',
                  name: docName,
                })),
              };
            }
          });

          setQuestionResponses(loadedResponses);
        }
      } catch (error) {
        console.error("Failed to load saved responses:", error);
      } finally {
        setLoading(false);
      }
    };

    if (companyName && areaPrefix) {
      loadSavedResponses();
    }
  }, [companyName, areaPrefix]);

  // Load inference question from Execution_INV_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_INV_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "PHYSICAL-VERIFICATION-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    if (companyName) {
      loadInferenceQuestion();
    }
  }, [companyName]);

  // Helper functions for questionnaire
  const updateSelectedOption = (questionId: string, value: string) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: value,
      },
    }));
  };

  const handleDocumentsChange = (questionId: string, documents: Array<{ id: string; type: string; name: string; content?: string; url?: string }>) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents,
      },
    }));
  };

  const handleTextResponseChange = (questionId: string, textResponse: { html: string; plainText: string } | null) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse,
      },
    }));
  };

  // Get period-end date from engagement
  const periodEndDate = currentEngagement?.reportingDate ? new Date(currentEngagement.reportingDate) : null;
  const managementDateObj = managementCountDate ? new Date(managementCountDate + 'T00:00:00') : null;
  const auditorDateObj = auditorCountDate ? new Date(auditorCountDate + 'T00:00:00') : null;

  // Helper to compare dates (ignoring time)
  const compareDates = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  };

  // Conditional logic
  const isManagementDateDifferentFromPeriodEnd = periodEndDate && managementDateObj && 
    !compareDates(managementDateObj, periodEndDate);
  const isAuditorDateDifferentFromManagement = managementDateObj && auditorDateObj && 
    !compareDates(auditorDateObj, managementDateObj);
  const isAuditorDateBlank = !auditorCountDate || auditorCountDate.trim() === "";

  // Save function
  const handleSave = useCallback(async () => {
    if (!companyName || !areaPrefix) {
      setSaveError("Missing required data (company name or area prefix)");
      setSaveStatus('error');
      return;
    }

    // Auto-save any pending textbox content
    const documentManagers = document.querySelectorAll('[data-document-manager]');
    for (const manager of Array.from(documentManagers)) {
      const itemId = manager.getAttribute('data-item-id');
      if (itemId) {
        const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
        if (autoSaveMethod && typeof autoSaveMethod === 'function') {
          autoSaveMethod();
        }
      }
    }

    await new Promise(resolve => setTimeout(resolve, 1000));

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const fileName = `Execution_${areaPrefix}_Responses.json`;

      // Load existing file first
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save dates
      existingData.substantiveProcedures["physical-verification-management-date"] = {
        selectedOption: "",
        textResponse: managementCountDate ? { html: managementCountDate, plainText: managementCountDate } : null,
        documents: [],
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      existingData.substantiveProcedures["physical-verification-auditor-date"] = {
        selectedOption: "",
        textResponse: auditorCountDate ? { html: auditorCountDate, plainText: auditorCountDate } : null,
        documents: [],
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      // Save all question responses
      Object.entries(questionResponses).forEach(([questionId, response]) => {
        const documentNames = (response.documents || []).map((d: any) => d.name || d);
        existingData.substantiveProcedures[questionId] = {
          selectedOption: response.selectedOption || "",
          textResponse: response.textResponse || null,
          documents: documentNames,
          table: null,
          lastUpdated: new Date().toISOString(),
          companyName,
        };
      });

      // Upload to SharePoint
      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: getFY_YEAR(),
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save responses");
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, areaPrefix, managementCountDate, auditorCountDate, questionResponses]);

  const run = async () => {
    if (!canRun) return;
    
    // Save column selections to file usage reference
    const entries: FileUsageEntry[] = [];
    const irFileName = irSelected.includes(' (') ? irSelected.split(' (')[0] : irSelected;
    const mcrFileName = mcrSelected.includes(' (') ? mcrSelected.split(' (')[0] : mcrSelected;
    entries.push({ "Search files - Inventory Register": irFileName });
    entries.push({ "Search files - Management Count Report": mcrFileName });
    const columnSelections: Record<string, any> = {
      irUniqueCol,
      mcrUniqueCol,
      columnMappings: rows.map(r => ({ left: r.left, right: r.right }))
    };
    entries.push({ "Column Selections": JSON.stringify(columnSelections) });
    if (entries.length > 0) {
      await updateProcedureFileUsageForWorkspace("Inventory", "Physical Verification", entries);
      await loadFileUsageReference();
    }
    
    setStatus("running");
    const mcp_cols = rows.map(r => r.left as string);
    const ir_cols = rows.map(r => r.right as string);
    try {
      if (window.payroll?.run) {
        const result = await window.payroll.run("execute_inv_physical_verification", {
          inputFiles: [],
          options: { mcp_unq_col: mcrUniqueCol, ir_unq_col: irUniqueCol, mcp_cols, ir_cols },
        });
        if (result.ok) {
          setStatus("completed");
        } else {
          setStatus("error");
        }
      } else setStatus("error");
    } catch { setStatus("error"); }
  };

  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const fileName = `Execution_INV_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "PHYSICAL-VERIFICATION-INFERENCE";
      const sectionToFind = `Execution_INV_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution INV Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_INV_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Physical Verification - Inventory</h2>
          <p className="text-gray-400">Map Management Count Report to Inventory Register and run completeness check</p>
        </div>
        {onBack && (<Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>)}
      </div>

      {/* Inventory Register */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Search className="h-5 w-5 text-blue-500" />Step 1: Search for Inventory Register</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 1: Search for Inventory Register"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {irStatus === "found" && (<span className="text-green-400 font-bold"> FOUND!</span>)}
            {irStatus === "not_found" && (<span className="text-yellow-400">No files found</span>)}
          </div>
          {irStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Inventory Register</Label>
                <Select value={irSelected} onValueChange={setIrSelected}>
                  <SelectTrigger><SelectValue placeholder="Select file" /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{irFiles.map((f)=> (<SelectItem key={f} value={f}>{f}</SelectItem>))}                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search files - Inventory Register"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search files - Inventory Register"])?.["Search files - Inventory Register"]}
                  </p>
                )}
              </div>
              {irSelected && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadIRColumns} disabled={!irSelected || loadingColsIR} className="bg-blue-600 hover:bg-blue-700 text-white">{loadingColsIR ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>) : ("Load Columns")}</Button>
                  {irColumns.length>0 && (<span className="text-green-400 text-sm"> {irColumns.length} columns loaded</span>)}
                </div>
              )}
            </div>
          )}
          {irStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2"><Label className="text-white">Reference Value (optional)</Label><Input value={refIR} onChange={(e)=>setRefIR(e.target.value)} placeholder="Enter reference value" className="border-white/10 bg-black/40 text-white" /></div>
                <Button onClick={uploadIR} disabled={upIR === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">{upIR === "uploading" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="h-4 w-4 mr-2" /> Upload File</>)}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Management Count Report */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Search className="h-5 w-5 text-blue-500" />Step 2: Search for Management Count Report</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 2: Search for Management Count Report"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {mcrStatus === "found" && (<span className="text-green-400 font-bold"> FOUND!</span>)}
            {mcrStatus === "not_found" && (<span className="text-yellow-400">No files found</span>)}
          </div>
          {mcrStatus === "found" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-white">Select Management Count Report</Label>
                <Select value={mcrSelected} onValueChange={setMcrSelected}>
                  <SelectTrigger><SelectValue placeholder="Select file" /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{mcrFiles.map((f)=> (<SelectItem key={f} value={f}>{f}</SelectItem>))}                  </SelectContent>
                </Select>
                {fileUsageReference.find(e => e["Search files - Management Count Report"]) && (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {fileUsageReference.find(e => e["Search files - Management Count Report"])?.["Search files - Management Count Report"]}
                  </p>
                )}
              </div>
              {mcrSelected && (
                <div className="flex items-center gap-3">
                  <Button onClick={loadMCRColumns} disabled={!mcrSelected || loadingColsMCR} className="bg-blue-600 hover:bg-blue-700 text-white">{loadingColsMCR ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading Columns...</>) : ("Load Columns")}</Button>
                  {mcrColumns.length>0 && (<span className="text-green-400 text-sm"> {mcrColumns.length} columns loaded</span>)}
                </div>
              )}
            </div>
          )}
          {mcrStatus === "not_found" && (
            <div className="space-y-4 p-4 border border-yellow-500/20 bg-yellow-500/5 rounded-lg">
              <div className="text-yellow-400 font-medium">File not found. Please upload a file.</div>
              <div className="space-y-3">
                <div className="space-y-2"><Label className="text-white">Reference Value (optional)</Label><Input value={refMCR} onChange={(e)=>setRefMCR(e.target.value)} placeholder="Enter reference value" className="border-white/10 bg-black/40 text-white" /></div>
                <Button onClick={uploadMCR} disabled={upMCR === "uploading"} className="bg-green-600 hover:bg-green-700 text-white">{upMCR === "uploading" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="h-4 w-4 mr-2" /> Upload File</>)}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unique Columns */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 3: Select Unique Columns</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 3: Select Unique Columns"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-white">Inventory Register Unique Column</Label>
            <Select value={irUniqueCol} onValueChange={setIrUniqueCol} disabled={irColumns.length===0}>
              <SelectTrigger><SelectValue placeholder={irColumns.length===0?"Load columns first":"Select column"} /></SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">{irColumns.map(c=>(<SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
            {fileUsageReference.find(e => e["Column Selections"]) && (() => {
              try {
                const selections = JSON.parse(fileUsageReference.find(e => e["Column Selections"])?.["Column Selections"] || "{}");
                const lastUsed = selections["Inventory Register Unique Column"];
                return lastUsed ? (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {lastUsed}
                  </p>
                ) : null;
              } catch {
                return null;
              }
            })()}
          </div>
          <div className="space-y-2">
            <Label className="text-white">Management Count Report Unique Column</Label>
            <Select value={mcrUniqueCol} onValueChange={setMcrUniqueCol} disabled={mcrColumns.length===0}>
              <SelectTrigger><SelectValue placeholder={mcrColumns.length===0?"Load columns first":"Select column"} /></SelectTrigger>
              <SelectContent className="border-white/10 bg-black/90 text-white">{mcrColumns.map(c=>(<SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>))}</SelectContent>
            </Select>
            {fileUsageReference.find(e => e["Column Selections"]) && (() => {
              try {
                const selections = JSON.parse(fileUsageReference.find(e => e["Column Selections"])?.["Column Selections"] || "{}");
                const lastUsed = selections["Management Count Report Unique Column"];
                return lastUsed ? (
                  <p className="text-xs text-white/60 mt-1">
                    Last used: {lastUsed}
                  </p>
                ) : null;
              } catch {
                return null;
              }
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Mapping Table */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Step 4: Map Columns to Test</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Step 4: Map Columns to Test"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-white">Management Count Report</Label>
                <Select value={row.left || ""} onValueChange={(v)=> setRows(prev=> prev.map((r,i)=> i===idx? { ...r, left: v }: r))} disabled={mcrColumns.length===0}>
                  <SelectTrigger><SelectValue placeholder={mcrColumns.length===0?"Load columns first":"Select column"} /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{mcrColumns.map(c=>(<SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
                {idx === 0 && fileUsageReference.find(e => e["Column Selections"]) && (() => {
                  try {
                    const selections = JSON.parse(fileUsageReference.find(e => e["Column Selections"])?.["Column Selections"] || "{}");
                    const mappings = selections["Mapping Rows"] || [];
                    const lastUsed = mappings[0]?.left;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>
              <div className="space-y-1">
                <Label className="text-white">Inventory Register</Label>
                <Select value={row.right || ""} onValueChange={(v)=> setRows(prev=> prev.map((r,i)=> i===idx? { ...r, right: v }: r))} disabled={irColumns.length===0}>
                  <SelectTrigger><SelectValue placeholder={irColumns.length===0?"Load columns first":"Select column"} /></SelectTrigger>
                  <SelectContent className="border-white/10 bg-black/90 text-white">{irColumns.map(c=>(<SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>))}</SelectContent>
                </Select>
                {idx === 0 && fileUsageReference.find(e => e["Column Selections"]) && (() => {
                  try {
                    const selections = JSON.parse(fileUsageReference.find(e => e["Column Selections"])?.["Column Selections"] || "{}");
                    const mappings = selections["Mapping Rows"] || [];
                    const lastUsed = mappings[0]?.right;
                    return lastUsed ? (
                      <p className="text-xs text-white/60 mt-1">
                        Last used: {lastUsed}
                      </p>
                    ) : null;
                  } catch {
                    return null;
                  }
                })()}
              </div>
            </div>
          ))}
          <div>
            <Button variant="outline" onClick={()=> setRows(prev=> [...prev, {}])} className="border-white/10 text-white"><Plus className="h-4 w-4 mr-2" />Add Row</Button>
          </div>
        </CardContent>
      </Card>

      {/* Run */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3 text-white"><Play className="h-5 w-5 text-green-500" />Run Completeness Check</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Run Completeness Check"))}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Button onClick={run} disabled={!canRun || status === "running"} className="bg-green-600 hover:bg-green-700 text-white">{status === "running" ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>) : ("Run Completeness Check")}</Button>
            {status === "completed" && (<span className="text-green-400">Successful</span>)}
            {status === "error" && (<span className="text-red-400">Failed</span>)}
          </div>
        </CardContent>
      </Card>

      {/* Questionnaire Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="text-white">Physical Verification Questionnaire</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Main Question */}
          <div className="space-y-4">
            <Label className="text-white font-medium">
              Have we received sufficient and appropriate audit evidence regarding the existence and condition of inventory by:
            </Label>
            <div className="ml-6 space-y-2 text-white/80 text-sm">
              <div>1. Attendance at physical inventory counting, unless impracticable, to:</div>
              <div className="ml-6 space-y-1">
                <div>(a) Evaluate management's instructions and procedures for recording and controlling the results of entity's physical inventory counting;</div>
                <div>(b) Observe the performance of management's count procedures;</div>
                <div>(c) Inspect the inventory count;</div>
                <div>(d) Perform test counts; and</div>
              </div>
              <div>2. Perform audit procedures over the entity's final inventory records to determine whether they accurately reflect actual inventory count results.</div>
            </div>
            <div className="ml-8 mt-4">
              <DocumentManager
                sectionKey="execution-substantive:Inventory"
                itemId="physical-verification-evidence"
                itemQuestion="Have we received sufficient and appropriate audit evidence regarding the existence and condition of inventory"
                itemTextResponse={questionResponses["physical-verification-evidence"]?.textResponse || null}
                itemDocuments={questionResponses["physical-verification-evidence"]?.documents || []}
                onDocumentsChange={(docs) => handleDocumentsChange("physical-verification-evidence", docs)}
                onTextResponseChange={(textResp) => handleTextResponseChange("physical-verification-evidence", textResp)}
                disabled={false}
              />
            </div>
          </div>

          {/* Date Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-white">Management's date of physical inventory count</Label>
              <Input
                type="date"
                value={managementCountDate}
                onChange={(e) => setManagementCountDate(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white">Auditor's date of performing physical inventory count</Label>
              <Input
                type="date"
                value={auditorCountDate}
                onChange={(e) => setAuditorCountDate(e.target.value)}
                className="border-white/10 bg-black/40 text-white"
              />
            </div>
          </div>

          {/* Conditional Question 1: Management date different from period-end */}
          {isManagementDateDifferentFromPeriodEnd && (
            <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-white/5">
              <Label className="text-white font-medium">
                Have we obtained sufficient and appropriate audit evidence about whether changes in inventory between count date and date of financial statements is properly recorded?
              </Label>
              <div className="ml-4 text-sm text-white/70">
                Matters relevant when designing such audit procedures include:
                <div className="ml-4 mt-2 space-y-1">
                  <div>1. Whether perpetual inventory records are properly adjusted.</div>
                  <div>2. Reliability of entity's perpetual inventory records.</div>
                  <div>3. Reasons for significant differences between physical count & perpetual records.</div>
                </div>
              </div>
              <RadioGroup
                value={questionResponses["physical-verification-inventory-changes"]?.selectedOption || ""}
                onValueChange={(value) => updateSelectedOption("physical-verification-inventory-changes", value)}
                className="ml-8"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="Yes" id="inventory-changes-yes" className="border-white/20 text-[#4da3ff]" />
                  <Label htmlFor="inventory-changes-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="No" id="inventory-changes-no" className="border-white/20 text-[#4da3ff]" />
                  <Label htmlFor="inventory-changes-no" className="text-sm text-white/80 cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              {questionResponses["physical-verification-inventory-changes"]?.selectedOption && (
                <div className="ml-8 mt-4">
                  <DocumentManager
                    sectionKey="execution-substantive:Inventory"
                    itemId="physical-verification-inventory-changes"
                    itemQuestion="Have we obtained sufficient and appropriate audit evidence about whether changes in inventory between count date and date of financial statements is properly recorded?"
                    itemTextResponse={questionResponses["physical-verification-inventory-changes"]?.textResponse || null}
                    itemDocuments={questionResponses["physical-verification-inventory-changes"]?.documents || []}
                    onDocumentsChange={(docs) => handleDocumentsChange("physical-verification-inventory-changes", docs)}
                    onTextResponseChange={(textResp) => handleTextResponseChange("physical-verification-inventory-changes", textResp)}
                    disabled={false}
                  />
                </div>
              )}
            </div>
          )}

          {/* Conditional Question 2: Auditor date different from management date */}
          {isAuditorDateDifferentFromManagement && (
            <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-white/5">
              <Label className="text-white font-medium">
                Perform audit procedures to assess whether changes in inventory count are properly recorded and perform audit procedures on intervening transactions.
              </Label>
              <div className="ml-8 mt-4">
                <DocumentManager
                  sectionKey="execution-substantive:Inventory"
                  itemId="physical-verification-auditor-different"
                  itemQuestion="Perform audit procedures to assess whether changes in inventory count are properly recorded and perform audit procedures on intervening transactions"
                  itemTextResponse={questionResponses["physical-verification-auditor-different"]?.textResponse || null}
                  itemDocuments={questionResponses["physical-verification-auditor-different"]?.documents || []}
                  onDocumentsChange={(docs) => handleDocumentsChange("physical-verification-auditor-different", docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange("physical-verification-auditor-different", textResp)}
                  disabled={false}
                />
              </div>
            </div>
          )}

          {/* Conditional Question 3: Auditor date is blank */}
          {isAuditorDateBlank && (
            <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-white/5">
              <div className="text-white/80 text-sm mb-4">
                Attendance at physical inventory count may be impracticable due to factors such as nature and location of inventory. As explained in SA 200, matter of difficulty, time or cost involved is not in itself a valid basis for the auditor to omit a procedure for which there is no alternative or to satisfied with less than persuasive audit evidence.
              </div>
              <Label className="text-white font-medium">
                In such cases, perform alternate audit procedures.
              </Label>
              <div className="ml-8 mt-4">
                <DocumentManager
                  sectionKey="execution-substantive:Inventory"
                  itemId="physical-verification-auditor-blank"
                  itemQuestion="In such cases, perform alternate audit procedures"
                  itemTextResponse={questionResponses["physical-verification-auditor-blank"]?.textResponse || null}
                  itemDocuments={questionResponses["physical-verification-auditor-blank"]?.documents || []}
                  onDocumentsChange={(docs) => handleDocumentsChange("physical-verification-auditor-blank", docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange("physical-verification-auditor-blank", textResp)}
                  disabled={false}
                />
              </div>
            </div>
          )}

          {/* Sufficient Evidence Question */}
          <div className="space-y-4">
            <Label className="text-white font-medium">
              Is sufficient and appropriate audit evidence obtained?
            </Label>
            <RadioGroup
              value={questionResponses["physical-verification-sufficient-evidence"]?.selectedOption || ""}
              onValueChange={(value) => updateSelectedOption("physical-verification-sufficient-evidence", value)}
              className="ml-8"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="sufficient-evidence-yes" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="sufficient-evidence-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="sufficient-evidence-no" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="sufficient-evidence-no" className="text-sm text-white/80 cursor-pointer">No</Label>
              </div>
            </RadioGroup>
            {questionResponses["physical-verification-sufficient-evidence"]?.selectedOption === "No" && (
              <div className="ml-8 mt-2 p-3 border border-yellow-500/30 bg-yellow-500/10 rounded-lg">
                <p className="text-yellow-400 text-sm font-medium">
                  Note: Consider the impact on auditor's opinion in accordance with SA 705 as a result of limitation of scope.
                </p>
              </div>
            )}
          </div>

          {/* Third Party Question */}
          <div className="space-y-4">
            <Label className="text-white font-medium">
              Is any inventory held by third party?
            </Label>
            <RadioGroup
              value={questionResponses["physical-verification-third-party"]?.selectedOption || ""}
              onValueChange={(value) => updateSelectedOption("physical-verification-third-party", value)}
              className="ml-8"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="third-party-yes" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="third-party-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="third-party-no" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="third-party-no" className="text-sm text-white/80 cursor-pointer">No</Label>
              </div>
            </RadioGroup>

            {/* Third Party Follow-up Questions */}
            {questionResponses["physical-verification-third-party"]?.selectedOption === "Yes" && (
              <div className="ml-8 mt-4 space-y-4 p-4 border border-white/10 rounded-lg bg-white/5">
                <Label className="text-white font-medium">
                  Has any doubt been placed over the integrity and objectivity of the third party?
                </Label>
                <RadioGroup
                  value={questionResponses["physical-verification-third-party-doubt"]?.selectedOption || ""}
                  onValueChange={(value) => updateSelectedOption("physical-verification-third-party-doubt", value)}
                  className="ml-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Yes" id="third-party-doubt-yes" className="border-white/20 text-[#4da3ff]" />
                    <Label htmlFor="third-party-doubt-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="No" id="third-party-doubt-no" className="border-white/20 text-[#4da3ff]" />
                    <Label htmlFor="third-party-doubt-no" className="text-sm text-white/80 cursor-pointer">No</Label>
                  </div>
                </RadioGroup>

                {questionResponses["physical-verification-third-party-doubt"]?.selectedOption === "Yes" && (
                  <div className="ml-4 mt-4 space-y-2">
                    <div className="text-white/80 text-sm">
                      Perform other audit procedures instead of or in addition to confirmation with third party. Other audit procedures may include:
                    </div>
                    <div className="ml-4 text-sm text-white/70 space-y-1">
                      <div>1. Attending, or arranging for another auditor to attend, the third party's physical counting of inventory, if practicable.</div>
                      <div>2. Obtaining another auditor's report, or a service auditor's report, on the adequacy of the third party's internal control for ensuring that inventory is properly counted and adequately safeguarded.</div>
                      <div>3. Inspecting documentation regarding inventory held by third parties, for example, warehouse receipts.</div>
                      <div>4. Requesting confirmation from other parties when inventory has been pledged as collateral.</div>
                    </div>
                    <div className="mt-4">
                      <DocumentManager
                        sectionKey="execution-substantive:Inventory"
                        itemId="physical-verification-third-party-procedures"
                        itemQuestion="Perform other audit procedures instead of or in addition to confirmation with third party"
                        itemTextResponse={questionResponses["physical-verification-third-party-procedures"]?.textResponse || null}
                        itemDocuments={questionResponses["physical-verification-third-party-procedures"]?.documents || []}
                        onDocumentsChange={(docs) => handleDocumentsChange("physical-verification-third-party-procedures", docs)}
                        onTextResponseChange={(textResp) => handleTextResponseChange("physical-verification-third-party-procedures", textResp)}
                        disabled={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button
          onClick={handleSave}
          className="flex items-center gap-2"
          disabled={saving || !companyName || !areaPrefix}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Responses
            </>
          )}
        </Button>
      </div>

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
            <div className="flex items-center gap-2">
              {inferenceSaveStatus === 'saved' && (
                <span className="text-sm text-green-400">Saved!</span>
              )}
              {inferenceSaveStatus === 'error' && inferenceSaveError && (
                <span className="text-sm text-red-400">Error</span>
              )}
              <Button
                onClick={saveInferenceQuestion}
                disabled={inferenceSaveStatus === 'saving' || !companyName}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {inferenceSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="inventory"
            itemId="PHYSICAL-VERIFICATION-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Step 1: Search for Inventory Register")]: "Step 1: Search for Inventory Register",
              [getSectionQuestionId("Step 2: Search for Management Count Report")]: "Step 2: Search for Management Count Report",
              [getSectionQuestionId("Step 3: Select Unique Columns")]: "Step 3: Select Unique Columns",
              [getSectionQuestionId("Step 4: Map Columns to Test")]: "Step 4: Map Columns to Test",
              [getSectionQuestionId("Run Completeness Check")]: "Run Completeness Check",
              [getSectionQuestionId("Physical Verification Questionnaire")]: "Physical Verification Questionnaire",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={workspaceName}
          subPhase={procedureName}
        />
      )}
    </div>
  );
}


